import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { FadeUp, Overline, MaskedLine } from "../components/Reveal";
import { EditorialMarquee } from "../components/Marquee";
import { ProjectTile } from "../components/ProjectTile";
import { PROJECTS } from "../lib/site";

const WorkHero = () => (
  <section data-testid="work-hero" className="pt-48 pb-24 md:pb-32 relative overflow-hidden">
    <div className="aurora aurora-float w-[22rem] h-[22rem] -top-10 right-0 opacity-25 -z-10" style={{ background: "#E0146B" }} aria-hidden="true" />
    <div className="aurora aurora-float w-[18rem] h-[18rem] bottom-0 -left-16 opacity-20 -z-10" style={{ background: "#0096C7", animationDelay: "6s" }} aria-hidden="true" />
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <MaskedLine delay={0.15}>
        <Overline>Selected work</Overline>
      </MaskedLine>
      <h1 className="mt-8 font-display font-black text-[11vw] md:text-[6.8vw] leading-[0.92] tracking-tighter" data-testid="work-heading">
        <MaskedLine delay={0.3}>PROOF,</MaskedLine>
        <MaskedLine delay={0.45} innerClassName="brand-gradient-text">NOT PROMISES.</MaskedLine>
      </h1>
      <MaskedLine delay={0.65} className="max-w-xl mt-10">
        <p className="text-base md:text-lg text-slate-600 leading-relaxed">
          A snapshot of the products we've designed, engineered and scaled with our partners.
        </p>
      </MaskedLine>
    </div>
  </section>
);

const ProjectCard = ({ p, i, wide }) => (
  <FadeUp delay={(i % 2) * 0.1} className={wide ? "md:col-span-8" : "md:col-span-4"}>
    <article
      data-testid={`project-card-${p.name.toLowerCase()}`}
      style={{ "--c": p.color }}
      className="group bg-white border border-ink/10 rounded-2xl overflow-hidden transition-[border-color,transform] duration-500 hover:border-[color:var(--c)] hover:-translate-y-1"
    >
      <div className="relative overflow-hidden">
        {p.image ? (
          <>
            <img
              src={p.image}
              alt={p.name}
              className={`w-full object-cover transition-transform duration-700 group-hover:scale-105 ${wide ? "h-72 md:h-96" : "h-72"}`}
            />
            <div className="absolute inset-0 bg-black/40 transition-colors duration-500 group-hover:bg-black/20" />
          </>
        ) : (
          <ProjectTile monogram={p.monogram} color={p.color} className={wide ? "h-72 md:h-96" : "h-72"} />
        )}
        <span className="absolute top-5 right-5 font-mono text-[11px] tracking-[0.2em] text-slate-600 bg-white/70 backdrop-blur-md border border-ink/10 rounded-full px-3 py-1.5">
          {p.year}
        </span>
      </div>
      <div className="p-8">
        <p className="font-mono text-[11px] uppercase tracking-[0.25em]" style={{ color: p.color }}>{p.category}</p>
        <h2 className="mt-3 font-display font-bold text-2xl md:text-3xl tracking-tight">{p.name}</h2>
        <p className="mt-3 text-slate-600 text-sm leading-relaxed">{p.description}</p>
      </div>
    </article>
  </FadeUp>
);

const WorkGrid = () => (
  <section data-testid="work-grid" className="pb-32 md:pb-44">
    <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-6">
      {PROJECTS.map((p, i) => (
        <ProjectCard key={p.name} p={p} i={i} wide={i % 3 !== 2} />
      ))}
    </div>
  </section>
);

const WorkCta = () => (
  <section data-testid="work-cta" className="py-32 md:py-44 border-t border-ink/10 relative overflow-hidden">
    <div className="aurora aurora-float w-[20rem] h-[20rem] -bottom-10 right-10 opacity-20 -z-10" style={{ background: "#FF8A00" }} aria-hidden="true" />
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <FadeUp>
        <h2 className="font-display font-black text-4xl md:text-7xl tracking-tighter leading-[0.98] max-w-4xl">
          Your product could be <span className="text-outline-accent">next.</span>
        </h2>
        <Link
          to="/contact"
          data-testid="work-cta-button"
          className="group mt-12 inline-flex items-center gap-2 text-white font-semibold rounded-full pl-7 pr-6 py-4 bg-[linear-gradient(100deg,#FF8A00,#E0146B,#0096C7)] bg-[length:200%_100%] bg-left transition-[background-position] duration-500 hover:bg-right"
        >
          Start a project
          <ArrowUpRight className="w-5 h-5 transition-transform duration-300 group-hover:rotate-45" />
        </Link>
      </FadeUp>
    </div>
  </section>
);

export default function WorkPage() {
  return (
    <>
      <WorkHero />
      <WorkGrid />
      <EditorialMarquee />
      <WorkCta />
    </>
  );
}
